
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "CatalogImageCropModal",
  components: { VueCropper },
  props: {
    file: { type: File, default: null },
    aspectRatio: { type: Number, default: 1 },
    minCropWidth: { type: Number, default: 800 },
    minCropHeight: { type: Number, default: 800 },
    maxPublishedWidth: { type: Number, default: 1200 },
    maxPublishedHeight: { type: Number, default: 1200 },
    outputQuality: { type: Number, default: 0.85 },
    destinationLabel: { type: String, default: "catálogo" },
    emitCroppedFile: { type: Boolean, default: false },
  },
  data() {
    return {
      source: "",
      applying: false,
      baseZoom: 0,
      zoomLevel: 1,
      minZoomLevel: 1,
      initialZoomLevel: 1,
      sourceDimensions: { width: 0, height: 0 },
      quality: { width: 0, height: 0, valid: false, label: "insuficiente" },
    };
  },
  computed: {
    qualityFeedback() {
      if (!this.sourceDimensions.width || !this.sourceDimensions.height) return "";
      if (this.quality.valid) return `✓ Imagem adequada para o ${this.destinationLabel}.`;
      if (this.sourceDimensions.width < this.minCropWidth || this.sourceDimensions.height < this.minCropHeight) {
        return "⚠ A imagem possui resolução baixa para o recorte mínimo.";
      }
      return "⚠ A área selecionada está abaixo do mínimo. Reduza o zoom.";
    },
  },
  watch: {
    file(file) {
      if (this.source) URL.revokeObjectURL(this.source);
      this.source = file ? URL.createObjectURL(file) : "";
      this.quality = { width: 0, height: 0, valid: false, label: "insuficiente" };
      this.applying = false;
      this.baseZoom = 0;
      this.zoomLevel = 1;
      this.minZoomLevel = 1;
      this.initialZoomLevel = 1;
      this.sourceDimensions = { width: 0, height: 0 };
    },
  },
  beforeDestroy() {
    if (this.source) URL.revokeObjectURL(this.source);
  },
  methods: {
    onReady() {
      this.$nextTick(() => {
        const cropper = this.$refs.cropper;
        if (!cropper) return;
        const canvas = cropper.getCanvasData();
        const image = cropper.getImageData();
        const cropBox = cropper.getCropBoxData();
        if (!image.naturalWidth || !image.naturalHeight) return;
        this.sourceDimensions = { width: image.naturalWidth, height: image.naturalHeight };
        this.baseZoom = canvas.width / image.naturalWidth;
        const smallestCoverRatio = Math.max(
          cropBox.width / image.naturalWidth,
          cropBox.height / image.naturalHeight
        );
        this.minZoomLevel = Math.min(1, smallestCoverRatio / this.baseZoom);
        const availableZoom = Math.min(
          image.naturalWidth / this.minCropWidth,
          image.naturalHeight / this.minCropHeight
        );
        this.initialZoomLevel = Math.min(3, this.minZoomLevel * Math.max(1, Math.min(1.1, availableZoom)));
        this.zoomLevel = this.initialZoomLevel;
        cropper.zoomTo(this.baseZoom * this.initialZoomLevel);
        this.updateQuality();
      });
    },
    onZoom(event) {
      if (this.baseZoom && event && event.detail) {
        this.zoomLevel = Math.max(this.minZoomLevel, Math.min(3, event.detail.ratio / this.baseZoom));
      }
    },
    updateQuality() {
      const cropper = this.$refs.cropper;
      if (!cropper) return;
      const data = cropper.getData(true);
      const width = Math.round(data.width || 0);
      const height = Math.round(data.height || 0);
      const valid = width >= this.minCropWidth && height >= this.minCropHeight;
      this.quality = {
        width,
        height,
        valid,
        label: !valid ? "insuficiente" : width >= this.maxPublishedWidth && height >= this.maxPublishedHeight ? "excelente" : "boa",
      };
    },
    setZoom(value) {
      if (!this.$refs.cropper || !this.baseZoom) return;
      this.zoomLevel = Math.max(this.minZoomLevel, Math.min(3, Number(value)));
      this.$refs.cropper.zoomTo(this.baseZoom * this.zoomLevel);
    },
    stepZoom(delta) {
      this.setZoom(this.zoomLevel + delta);
    },
    reset() {
      if (!this.$refs.cropper || !this.baseZoom) return;
      this.$refs.cropper.reset();
      this.setZoom(this.initialZoomLevel);
    },
    apply() {
      if (!this.quality.valid || !this.$refs.cropper || this.applying) return;
      this.applying = true;
      const file = this.file;
      const data = this.$refs.cropper.getData(true);
      const crop = { x: data.x, y: data.y, width: data.width, height: data.height };
      const canvas = this.$refs.cropper.getCroppedCanvas({
        maxWidth: this.emitCroppedFile ? this.maxPublishedWidth : 600,
        maxHeight: this.emitCroppedFile ? this.maxPublishedHeight : 600,
      });
      if (!canvas) {
        this.applying = false;
        return;
      }
      canvas.toBlob((blob) => {
        this.applying = false;
        if (blob && this.file === file) {
          const result = { file, crop, preview: URL.createObjectURL(blob) };
          if (this.emitCroppedFile) {
            const name = file.name.replace(/\.[^.]+$/, "") + "-recorte.webp";
            result.croppedFile = new File([blob], name, { type: "image/webp" });
          }
          this.$emit("apply", result);
        } else if (!blob && this.file === file) {
          this.$toast.error("Não foi possível preparar a prévia da imagem.");
        }
      }, "image/webp", this.outputQuality);
    },
  },
};
